import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Inbox, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getDay, Payment, TYPE_LABEL, METHOD_LABEL, money } from '../finances/api';
import { Skeleton } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import { readCache, writeCache, pruneCacheGroup } from '../../services/localCache';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function hhmm(dt: string) {
  const m = String(dt || '').match(/[T ](\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

export default function TodayPaymentsPanel() {
  // Caché fechado: solo pinta al instante si es del MISMO día (los pagos de ayer no aplican).
  const cacheKey = `payday:${todayLocal()}`;
  const cached = readCache<{ rows: Payment[]; grand: number }>(cacheKey);
  const [rows, setRows] = useState<Payment[] | null>(cached?.rows ?? null);
  const [grand, setGrand] = useState(cached?.grand ?? 0);
  const { hasModule } = useAuth();
  // Importes OCULTOS por defecto, igual que la nómina (Carlos 29-sep-2026): el Inicio se ve en el mostrador.
  // Ojito o tocar el total para verlos; al salir o recargar vuelven a taparse.
  const [ver, setVer] = useState(false);
  const importe = (n: number | string) => (ver ? money(n) : '$ ****');

  useEffect(() => {
    getDay(todayLocal())
      .then((r) => {
        setRows(r.data); setGrand(r.totals.grand);
        writeCache(cacheKey, { rows: r.data, grand: r.totals.grand });
        pruneCacheGroup('payday', cacheKey);
      })
      .catch(() => setRows((prev) => prev ?? []));
  }, []);

  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <Wallet size={16} className="text-accent-ink" /> Pagos del día
          <button type="button" onClick={() => setVer((v) => !v)} data-testid="pagos-dia-ojito"
            title={ver ? 'Ocultar importes' : 'Ver importes'} aria-label={ver ? 'Ocultar importes' : 'Ver importes'} aria-pressed={ver}
            className="rounded-md p-1 text-content-3 transition hover:bg-surface-2 hover:text-content">
            {ver ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </h3>
        {/* La cajera (Caja sin Finanzas) va a los Ingresos de hoy de la Caja. */}
        {hasModule('letsmove_finances')
          ? <Link to="/finanzas" className="shrink-0 text-xs font-medium text-brand-blue hover:underline">Ver finanzas →</Link>
          : <Link to="/caja?t=ingresos" className="shrink-0 text-xs font-medium text-brand-blue hover:underline">Ver en Caja →</Link>}
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState compact icon={Inbox} title="Sin pagos hoy" subtitle="Aún no se registran pagos en el día." />
      ) : (
        <>
          <div className="mb-3 flex items-baseline gap-2">
            <button type="button" onClick={() => setVer((v) => !v)} data-testid="pagos-dia-total" title={ver ? 'Ocultar importes' : 'Ver importes'}
              className="font-display text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{importe(grand)}</button>
            <span className="text-xs text-content-3">{rows.length} {rows.length === 1 ? 'pago' : 'pagos'} hoy</span>
          </div>
          <ul className="-mr-1 max-h-72 space-y-1 overflow-y-auto pb-3 pr-1 [scrollbar-width:thin]">
            {rows.map((p) => (
              <li key={p.payment_id} className="flex items-center gap-2.5 rounded-lg px-1 py-1.5 transition hover:bg-surface-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-content">{p.payer || '—'}</div>
                  <div className="truncate text-xs text-content-3">
                    {TYPE_LABEL[p.type] || p.type}
                    {p.assoc_name ? ` · ${p.assoc_name}` : ''} · {METHOD_LABEL[p.method] || p.method}
                    {hhmm(p.datetime) ? ` · ${hhmm(p.datetime)}` : ''}
                  </div>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-content">{importe(p.amount)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
